import { Card, Stack, Group, Text, Title, Badge } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../i18n/I18nProvider';
import type { developer } from '@repletoryreach/sdk-api';
import { formatDate } from '../../../lib/format';

/**
 * What changed in each published version of this document, newest first.
 *
 * A version is not a marketing number: it is the thing a developer names when
 * they say "we built against this", which is why the version they built against
 * stays downloadable long after it stops being current.
 */
export function Changelog({
  releases,
  versions,
  loading
}: {
  releases: developer.PublicApiRelease[];
  versions: developer.PublicApiVersion[];
  loading: boolean;
}) {
  const t = useT();

  if (loading) {
    return (
      <Text role="caption" c="var(--rr-color-text-muted)">
        {t('common.loading')}
      </Text>
    );
  }

  if (releases.length === 0) {
    return <Alert color="info">{t('developers.changelog.empty')}</Alert>;
  }

  const currentVersion = versions.find((v) => v.current)?.version;

  return (
    <Stack gap="lg">
      {releases.map((release) => (
        <Card key={release.version} withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
              <Group gap="sm" align="center" wrap="wrap">
                <Title order={4}>{release.version}</Title>
                {release.version === currentVersion && (
                  <Badge color="success" variant="light">
                    {t('developers.changelog.current')}
                  </Badge>
                )}
              </Group>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {formatDate(release.released_on)}
              </Text>
            </Group>

            <Text role="body">{release.summary}</Text>

            <Stack gap="sm">
              {release.changes.map((change, index) => (
                <Group key={index} gap="sm" align="flex-start" wrap="nowrap">
                  <Badge
                    size="sm"
                    variant="light"
                    color={
                      change.kind === 'deprecated'
                        ? 'warning'
                        : change.kind === 'fixed'
                          ? 'info'
                          : change.kind === 'changed'
                            ? 'warning'
                            : 'success'
                    }
                    className="rr-dev__nowrap-badge"
                  >
                    {t(`developers.changelog.kind.${change.kind}`, change.kind)}
                  </Badge>
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {change.text}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
